import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Send, Sparkles, X, Mic, Square, Copy, Check, Volume2, Loader2, Languages } from "lucide-react";

const sampleQuestions = [
  "Can this be used outdoors?",
  "What sizes are available?",
  "How is it installed?",
  "Is it waterproof?",
];

export default function ChatWidget({ productCode, productName }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      english: `Hi, I'm Heda AI. I can help you learn more about ${
        productName || "this product"
      }. Ask me anything — type or tap the mic to speak in any language.`,
      original: null,
      language: "en",
      language_label: "EN",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    const newHistory = [...messages, { role: "user", english: q, original: q, language: "auto", language_label: "" }];
    setMessages(newHistory);
    setSending(true);
    try {
      const { data } = await api.post(`/public/products/${productCode}/chat-bilingual`, {
        message: q,
        history: newHistory
          .filter((m) => m.role === "user" || m.role === "assistant")
          .slice(-6)
          .map((m) => ({ role: m.role, content: m.english || m.original || "" })),
      });
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          english: "Sorry, I couldn't reach Heda AI right now. Please try again.",
          original: null,
          language: "en",
          language_label: "EN",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (blob) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append(
        "history",
        JSON.stringify(
          messages.slice(-6).map((m) => ({ role: m.role, content: m.english || m.original || "" })),
        ),
      );
      const token = localStorage.getItem("heda_token");
      const res = await fetch(`${API}/public/products/${productCode}/chat-voice`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessages((m) => [
        ...m,
        {
          role: "user",
          english: data.transcript,
          original: data.transcript,
          language: data.language,
          language_label: data.language_label,
        },
        { role: "assistant", ...data, transcript: undefined },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          english: "Couldn't understand the voice. Please try again or type your question.",
          original: null,
          language: "en",
          language_label: "EN",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Ask Heda AI dock */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 pointer-events-none"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="pointer-events-auto px-3 pt-3 pb-3 bg-white/40 backdrop-blur-xl">
          <button
            data-testid="chat-toggle-button"
            onClick={() => setOpen(true)}
            className="w-full flex items-center justify-center gap-2.5 px-5 py-3 text-white font-semibold text-[15px] tracking-tight bg-[#1A1A18] rounded-full hover:bg-[#2a2a26] transition-colors shadow-[0_10px_30px_rgba(26,26,24,0.18)]"
          >
            <Sparkles size={17} strokeWidth={2} className="text-[#B5953B]" />
            Ask Heda AI
          </button>
        </div>
      </div>

      <div className="h-20" aria-hidden="true" />

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#1A1A18]/30 md:bg-transparent flex md:items-end md:justify-end"
          onClick={() => setOpen(false)}
          data-testid="chat-overlay"
        >
          <div
            className="bg-white w-full md:w-[420px] md:m-6 md:rounded-sm md:border md:border-[#E8E6DF] md:shadow-2xl flex flex-col"
            style={{
              height: "100dvh",
              maxHeight: "100dvh",
              paddingTop: "env(safe-area-inset-top)",
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="border-b border-[#E8E6DF] px-4 py-3 flex items-center justify-between bg-white">
              <div className="flex items-center gap-2">
                <Sparkles size={16} strokeWidth={2} className="text-[#B5953B]" />
                <div>
                  <div className="font-semibold text-sm">Ask Heda AI</div>
                  <div className="text-[11px] text-[#5C5C54]">{productName || "Product specialist"}</div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[#5C5C54] hover:text-[#1A1A18]"
                data-testid="chat-close-button"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, idx) => (
                <ChatBubble key={idx} message={m} />
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="hed-typing bg-[#F2F0EA] border border-[#E8E6DF] px-4 py-3 rounded-2xl rounded-bl-md">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              {messages.length <= 1 && !sending && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {sampleQuestions.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      className="hed-chip hover:bg-[#E8E6DF] cursor-pointer transition-colors"
                      data-testid="chat-sample-question"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              className="border-t border-[#E8E6DF] p-3 flex items-center gap-2 bg-white w-full"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
              onSubmit={(e) => { e.preventDefault(); send(); }}
            >
              <MicButton onAudio={sendVoice} disabled={sending} />
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type or tap the mic…"
                className="flex-1 min-w-0 bg-[#FAFAF8] border border-[#E8E6DF] focus:border-[#B5953B] rounded-full px-4 py-2.5 text-[14px] outline-none"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={sending || !input.trim()}
                className="bg-[#B5953B] hover:bg-[#9A7E32] disabled:opacity-40 text-white p-2.5 rounded-full transition-colors flex-shrink-0"
              >
                <Send size={16} strokeWidth={2} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// ChatBubble — handles bilingual toggle, copy, TTS
// ============================================================
export function ChatBubble({ message }) {
  const isUser = message.role === "user";
  const hasBilingual =
    !isUser && message.original && message.language && message.language !== "en";
  const [showEnglish, setShowEnglish] = useState(false);
  const display = showEnglish || !message.original ? message.english : message.original;

  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(display || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const onPlay = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    try {
      const token = localStorage.getItem("heda_token");
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text: display }),
      });
      if (!res.ok) throw new Error("tts");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch {
      setPlaying(false);
    }
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={isUser ? "max-w-[80%]" : "max-w-[88%]"}>
        <div
          data-testid={`chat-message-${message.role}`}
          className={
            isUser
              ? "bg-[#1A1A18] text-white text-[14px] leading-relaxed px-3.5 py-2.5 rounded-2xl rounded-br-md whitespace-pre-line"
              : "bg-[#F2F0EA] text-[#1A1A18] text-[14px] leading-relaxed px-3.5 py-2.5 rounded-2xl rounded-bl-md border border-[#E8E6DF] whitespace-pre-line"
          }
        >
          <RenderWithLinks text={display || ""} />
        </div>
        {!isUser && (
          <div className="mt-1.5 ml-1 flex items-center gap-1 flex-wrap">
            {hasBilingual && (
              <button
                onClick={() => setShowEnglish((x) => !x)}
                title={showEnglish ? "Show original language" : "Show in English"}
                className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#E8E6DF] hover:border-[#B5953B] text-[#5C5C54] hover:text-[#5E6033] transition-colors"
                data-testid="bilingual-toggle"
              >
                <Languages size={10} strokeWidth={2} />
                {showEnglish ? message.language_label : "EN"}
              </button>
            )}
            <button
              onClick={onCopy}
              title="Copy this answer"
              className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#E8E6DF] hover:border-[#B5953B] text-[#5C5C54] hover:text-[#5E6033] transition-colors"
              data-testid="chat-copy"
            >
              {copied ? <Check size={10} strokeWidth={2} /> : <Copy size={10} strokeWidth={2} />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              onClick={onPlay}
              title="Play this answer"
              className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#E8E6DF] hover:border-[#B5953B] text-[#5C5C54] hover:text-[#5E6033] transition-colors"
              data-testid="chat-play"
            >
              {playing ? (
                <Loader2 size={10} strokeWidth={2} className="animate-spin" />
              ) : (
                <Volume2 size={10} strokeWidth={2} />
              )}
              {playing ? "Playing" : "Play"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Render assistant text with markdown links [text](/url) becoming clickable React Router links.
function RenderWithLinks({ text }) {
  if (!text) return null;
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    parts.push(
      <a
        key={key++}
        href={m[2]}
        className="font-semibold text-[#5E6033] hover:text-[#B5953B] underline underline-offset-2"
      >
        {m[1]}
      </a>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
  return <>{parts}</>;
}

// ============================================================
// MicButton — tap to record / tap again to stop using MediaRecorder
// ============================================================
function MicButton({ onAudio, disabled }) {
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 1000) onAudio(blob);
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      alert("Microphone access denied. Please allow microphone access to use voice chat.");
    }
  };
  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled && !recording}
      data-testid={recording ? "chat-mic-stop" : "chat-mic-start"}
      className={`p-2.5 rounded-full transition-colors flex-shrink-0 ${
        recording
          ? "bg-[#9E2A2B] hover:bg-[#7d2122] text-white animate-pulse"
          : "bg-[#F2F0EA] hover:bg-[#E8E6DF] text-[#1A1A18] border border-[#E8E6DF]"
      } disabled:opacity-40`}
      title={recording ? "Stop recording" : "Voice input"}
    >
      {recording ? <Square size={14} strokeWidth={2} /> : <Mic size={16} strokeWidth={2} />}
    </button>
  );
}
